import { EmailTemplate } from '@spike/email-marketing-model';
import Button from 'components/UI/V3/Button/Button';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { Table } from 'components/UI/V3/Table/Table';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableEmptyState } from 'components/UI/V3/Table/EmptyState/TableEmptyState';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './SavedTemplatesList.module.scss';

const PAGE_SIZE = 10;

interface SavedTemplatesListProps {
    onSelectTemplate: (template: EmailTemplate) => void;
    onStartFromScratch: () => void;
}

const formatDate = (iso: string): string => {
    if (!iso) return '';
    const m = moment(iso);
    return m.isValid() ? m.format('MMM DD, YYYY [at] hh:mm A') : '';
};

/**
 * Sub-tab "Email Saved" of the campaign wizard's step 2 picker.
 *
 * Lists the marketplace's own templates (scope=own, like the templates tab
 * list), with a thumbnail preview, search and pagination. "From Scratch" sits
 * at the top right and creates a brand-new template — the parent decides what
 * that does (e.g. open the editor as an embedded overlay).
 *
 * Empty state: no own templates yet → matches the design's centered prompt
 * with envelope icon + "Create a New Email Template" + From Scratch button.
 */
const SavedTemplatesList: FunctionComponent<SavedTemplatesListProps> = ({ onSelectTemplate, onStartFromScratch }) => {
    const list = useSelector((state: RootState) => state.emailTemplates.list);

    const [page, setPage] = useState<number>(1);
    const [searchText, setSearchText] = useState<string>('');

    // The wizard root (`AddEmailCampaign`) dispatches `fetchEmailTemplatesThunk`
    // on mount with no scope, so `list` contains both system seeds and own
    // templates by the time we render. This component slices to the own
    // templates via the `editable === true` filter — system templates carry
    // `editable: false` regardless of the viewing marketplace.
    const ownTemplates = useMemo(() => list.filter(template => template.editable === true), [list]);

    const filteredTemplates = useMemo(() => {
        if (searchText.length === 0) return ownTemplates;
        return ownTemplates.filter(template => template.name.toLowerCase().includes(searchText.toLowerCase()));
    }, [searchText, ownTemplates]);

    const paginatedTemplates = useMemo(
        () => filteredTemplates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredTemplates, page]
    );

    const totalPages = useMemo(() => Math.ceil(filteredTemplates.length / PAGE_SIZE), [filteredTemplates]);

    const hasTemplates = ownTemplates.length > 0;
    const isSearching = searchText.length > 0;
    const showEmptyState = !hasTemplates && !isSearching;

    if (showEmptyState) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyIconBadge}>
                    <FontAwesomeIcon
                        icon={faEnvelope}
                        className={styles.emptyIcon}
                    />
                </div>
                <h3 className={styles.emptyTitle}>
                    Create a New
                    <br />
                    Email Template
                </h3>
                <p className={styles.emptyDescription}>Click the &quot;From Scratch&quot; button to add one.</p>
                <Button
                    label="From Scratch"
                    variant="primary"
                    onClick={onStartFromScratch}
                    className={styles.emptyButton}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.toolbar}>
                <Button
                    label="From Scratch"
                    variant="primary"
                    onClick={onStartFromScratch}
                />
            </div>

            <div className={styles.searchContainer}>
                <SearchBar
                    searchText={searchText}
                    onChangeSearchText={setSearchText}
                    placeholder="Search by email name"
                />
            </div>

            {filteredTemplates.length === 0 ? (
                <Table className={styles.emptyResults}>
                    <TableBody>
                        <TableEmptyState>
                            <p>No templates found.</p>
                        </TableEmptyState>
                    </TableBody>
                </Table>
            ) : (
                <Table
                    pagination={{ page, totalPages }}
                    onPageChange={setPage}
                >
                    <TableHeader>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Created</TableHeaderCell>
                        <TableHeaderCell />
                    </TableHeader>
                    <TableBody>
                        {paginatedTemplates.map(template => (
                            <TableRow key={template.id ?? template.uuid}>
                                <TableCell>
                                    <div className={styles.emailCell}>
                                        <div className={styles.thumbnail}>
                                            {template.html ? (
                                                <iframe
                                                    className={styles.thumbnailIframe}
                                                    title={template.name}
                                                    srcDoc={template.html}
                                                    sandbox=""
                                                    scrolling="no"
                                                />
                                            ) : (
                                                <div className={styles.thumbnailPlaceholder} />
                                            )}
                                        </div>
                                        <p className={styles.title}>{template.name}</p>
                                    </div>
                                </TableCell>
                                <TableCell>{formatDate(template.createdAt)}</TableCell>
                                <TableCell isActions>
                                    <Button
                                        label="Use Template"
                                        variant="outlined"
                                        onClick={() => onSelectTemplate(template)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};

export default SavedTemplatesList;
